import { useCallback, useEffect, useRef, useState } from "react";

const defaultVideoConstraints = {
  facingMode: "user",
  width: 1024,
  height: 1024,
};

export const useAvatarAiCameraCapture = ({
  enabled = true,
  onCapture,
  onError,
  onTrack,
  videoConstraints = defaultVideoConstraints,
} = {}) => {
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const captureRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setCountdown(null);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false,
      });
      streamRef.current = stream;
      setCameraActive(true);
      setCameraAvailable(true);
      onTrack?.("avatar_ai_camera_started", {
        cameraAvailable: true,
      });
      return stream;
    } catch (err) {
      setCameraAvailable(false);
      onError?.(err);
      onTrack?.("avatar_ai_source_upload_failed", {
        source: "camera",
        reason: "camera_unavailable",
      });
      return null;
    }
  }, [onError, onTrack, stopCamera, videoConstraints]);

  useEffect(() => {
    if (!enabled) {
      stopCamera();
      return undefined;
    }

    let cancelled = false;

    const checkCamera = async () => {
      try {
        if (!navigator.mediaDevices?.enumerateDevices) {
          if (!cancelled) setCameraAvailable(false);
          return;
        }
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some((device) => device.kind === "videoinput");
        if (!cancelled) setCameraAvailable(hasCamera);
      } catch {
        if (!cancelled) setCameraAvailable(false);
      }
    };

    checkCamera();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [enabled, stopCamera]);

  useEffect(() => {
    if (!cameraActive) return undefined;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return undefined;
    video.srcObject = stream;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {});
    }
    return () => {
      video.srcObject = null;
    };
  }, [cameraActive]);

  const captureFromCamera = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const size = Math.min(video.videoWidth, video.videoHeight);

    if (!size) {
      onError?.(new Error("Camera non pronta. Riprova tra un secondo."));
      return;
    }

    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          onError?.(new Error("Impossibile catturare l'immagine."));
          return;
        }
        const file = new File([blob], `capture-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        stopCamera();
        onTrack?.("avatar_ai_camera_captured", {
          source: "camera",
        });
        await onCapture?.(file);
      },
      "image/jpeg",
      0.92,
    );
  }, [onCapture, onError, onTrack, stopCamera]);

  captureRef.current = captureFromCamera;

  const startCountdown = useCallback(() => {
    if (!cameraActive) return;
    setCountdown(3);
  }, [cameraActive]);

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown <= 0) {
      captureRef.current?.();
      setCountdown(null);
      return undefined;
    }
    const id = setTimeout(() => {
      setCountdown((current) =>
        typeof current === "number" ? current - 1 : current,
      );
    }, 900);
    return () => clearTimeout(id);
  }, [countdown]);

  return {
    cameraActive,
    cameraAvailable,
    countdown,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    startCountdown,
  };
};
